<template>
  <div :class="$style['form-phone']">
    <LazySearchDropdown
      v-if="langOptions"
      :class="$style['dropdown']"
      :items="filteredOptions?.length ? filteredOptions : langOptions"
      :searchable-data="langOptions.map(item => item.name)"
      :selected="selectedLang"
      bordered
      @input="item => onChange(item)"
      @results="filteredItems => handleSearchResults(filteredItems)">
      <template
        v-if="selectedLang"
        #selected>
        <img :src="`/flags/${selectedLang.code.toLowerCase()}.svg`">
        {{ selectedLang.dial_code }}
      </template>
      <template #item="{ item }">
        <img :src="`/flags/${item.code.toLowerCase()}.svg`">
        {{ item.name }} ( {{ item.dial_code }} )
      </template>
    </LazySearchDropdown>
    <FormInput
      ref="input"
      v-bind="$attrs"
      :model-value="modelValue"
      :class="$style['form-input']"
      validate
      :validation="validation"
      type="tel"
      inputmode="tel"
      autocomplete="tel"
      @blur="onBlur"
      @update:model-value="onInput" />
  </div>
</template>
<script setup>
import { parsePhoneNumber } from 'libphonenumber-js'
const { $lang } = useNuxtApp()

defineOptions({
  inheritAttrs: false
})

const emit = defineEmits(['update:modelValue', 'blur'])

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null
  },
  validate: {
    type: Boolean,
    default: false
  },
  country: {
    type: String,
    default: 'NL'
  },
  validation: {
    type: [Boolean, Object],
    default: false
  }
})

const input = ref(null)
const countries = ref(null)
const lang = ref(null)
const langOptions = ref(null)
const filteredOptions = ref(null)

const onInput = value => {
  try {
    const number = parsePhoneNumber(value, lang.value)
    if (number.country !== lang.value) {
      lang.value = number.country
    }
    emit('update:modelValue', number.number)
  } catch (e) {
    emit('update:modelValue', value)
  }
}

const handleSearchResults = filteredItems => {
  filteredOptions.value = filteredItems.map(item => {
    return langOptions.value.find(langOption => langOption.name === item)
  })
}

const onBlur = () => {
  emit('blur')
}

const onChange = value => {
  lang.value = value.code
  emit('update:modelValue', '')
  setTimeout(() => {
    input.value.focus()
  }, 10)
}

const selectedLang = computed(() => {
  if (!langOptions.value) { return false }
  return langOptions.value.find(langOption => {
    return langOption.code === lang.value
  })
})

watch(() => props.modelValue, () => {
  try {
    if (!lang.value && props.modelValue) {
      const number = parsePhoneNumber(props.modelValue, lang.value)
      if (number) {
        lang.value = number.country || props.country
      }
    }
    if (!props.modelValue && !lang.value && props.country) {
      lang.value = props.country
    }
  } catch (e) {
    lang.value = props.country
  }
}, { immediate: true })

onMounted(async () => {
  await import(`../../assets/countries/${$lang}.json`).then(
    data => {
      countries.value = data.default
    }
  )

  await import(`../../assets/countries/phonePrefix-${$lang}.json`).then(data => {
    langOptions.value = data.default
    langOptions.value.sort((a, b) => {
      return a.code < b.code ? -1 : 1
    })
    langOptions.value.sort(a => {
      return a.priority ? -1 : 1
    })
  })
  if (!props.modelValue && props.country) {
    lang.value = props.country
  }
})
</script>
<style lang="scss" module>
.form-phone {
  position: relative;
  display: flex;
  z-index: z-index(toolbar) + 1;

  .dropdown {
    background-color: $white;
    width: $spacing * 16;
    height: $spacing * 6;
    position: relative;
    z-index: 2;

    & > *:not(button) {
      border-radius: $border-radius 0 0 $border-radius;
    }
  }
}

.form-input {
  flex: 1;
  width: 100%;
  min-height: $spacing * 6;
  position: relative;
  z-index: 2;

  input {
    border-top-left-radius: 0;
    border-left-color: transparent;
    height: 0;
    border-bottom-left-radius: 0;
  }
}

</style>
